import { useState, useEffect, useCallback, useMemo } from 'react';
import { useRole } from '../../hooks/useRole';

export const useAllRolesViewModel = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const { cacheOperations: { readRoles } } = useRole()
  const {
    getRoles,
    deleteRole,
    createRole,
    updateRole: apiUpdateRole
  } = useRole();


  const fetchRoles = useCallback(async () => {
    if (!loading) setLoading(true);
    try {
      const result = await getRoles();
      if (result.success) {
        setRoles(result.data);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getRoles]);

  // Only fetch on mount
  useEffect(() => {
    fetchRoles();
  }, []); // Remove fetchRoles from dependencies

  const getRoleName = useMemo(
    () => (roleId) => {
      const role = readRoles();

      const roleName = role.find(
        (rol) => rol.role_id.toString() === roleId?.toString()
      );

      return roleName?.role_name || "Unknown";
    },
    [readRoles]
  );

  const handleDelete = useCallback(async (role) => {
    try {
      const result = await deleteRole(parseInt(role.role_id));
      if (result.success) {
        await fetchRoles();
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError(err.message);
    }
  }, [deleteRole, fetchRoles]);

  const handleCreateRole = useCallback(async (formData) => {
    try {
      const result = await createRole(formData);
      if (result.success) {
        await fetchRoles();
        return { success: true };
      }
    } catch (error) {
      // Pass through the error message
      throw new Error(error.message);
    }
  }, [createRole, fetchRoles]);

  const updateRole = useCallback(async (roleId, formData) => {
    try {
      const result = await apiUpdateRole(roleId, formData);
      if (result.success) {
        await fetchRoles();
        return { success: true };
      }
    } catch (error) {
      // Pass through the error message
      throw new Error(error.message);
    }
  }, [apiUpdateRole, fetchRoles]);


  return {
    roles,
    loading: loading,
    error,
    refetch: fetchRoles,
    handleDelete,
    createRole: handleCreateRole,
    updateRole,
    getRoleName
  };
}; 